import { useGetDevicesQuery } from 'Services/ServiceProviderManagement/Features/Device/deviceApi'
import type { FetchDeviceListParams } from 'Services/ServiceProviderManagement/Features/Device/types'
import { PTA_PARAM_KEY, ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export const SELECTED_DEVICE_PARAMETER_KEY = 'selectedDevice'

function useDevicesWithParams() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [searchQueryParams, setSearchQueryParams] = useState<FetchDeviceListParams>({
    name: searchParams.get('name') ?? undefined,
    search_text: searchParams.get('search_text') ?? undefined,
    sort_by: searchParams.get('sort_by') ?? 'deviceId',
    sort_order: searchParams.get('sort_order') ?? SORT_ORDER_ASC,
    start_index: searchParams.get('start_index') ?? undefined,
    pta: searchParams.get('pta') ?? undefined,
    count: ROWS_PER_PAGE_DEFAULT
  })

  const selectedDeviceQueryParam = searchParams.get(SELECTED_DEVICE_PARAMETER_KEY) ?? undefined
  const [selectedDeviceId, setSelectedDeviceId] = useState(selectedDeviceQueryParam)

  const selectedDevicePtaQueryParam = searchParams.get(PTA_PARAM_KEY) ?? undefined
  const [selectedPta, setSelectedPta] = useState(selectedDevicePtaQueryParam)

  const {
    data: { resources: devices } = { resources: [] },
    data: { total_results: totalResults } = { total_results: 0 },
    isFetching
  } = useGetDevicesQuery(searchQueryParams)

  const setQueryParams = () => {
    Object.entries(searchQueryParams).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value.toString())
      } else {
        searchParams.delete(key)
      }
    })

    if (selectedDeviceId) {
      searchParams.set(SELECTED_DEVICE_PARAMETER_KEY, selectedDeviceId)
    } else {
      searchParams.delete(SELECTED_DEVICE_PARAMETER_KEY)
    }

    if (selectedPta) {
      searchParams.set(PTA_PARAM_KEY, selectedPta)
    } else {
      searchParams.delete(PTA_PARAM_KEY)
    }

    setSearchParams(searchParams)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: not needed because we only want to run this when params change
  useEffect(() => {
    setQueryParams()
  }, [searchQueryParams])

  // biome-ignore lint/correctness/useExhaustiveDependencies: not needed because we only want to run this when selectedSalesPointId changes
  useEffect(() => {
    if (selectedDeviceId) {
      setQueryParams()
    } else {
      searchParams.delete(SELECTED_DEVICE_PARAMETER_KEY)
      searchParams.delete(PTA_PARAM_KEY)
      setSearchParams(searchParams)
    }
  }, [selectedDeviceId, selectedPta])

  return {
    devices,
    isFetching,
    searchQueryParams,
    setSearchQueryParams,
    totalResults,
    selectedDeviceId,
    setSelectedDeviceId,
    selectedPta,
    setSelectedPta
  }
}

export default useDevicesWithParams
